// React
import React, { useState, useContext, useLayoutEffect, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// MUI
import { Stack, Divider } from "@mui/material";
// Others
import { useSnackbar } from "notistack";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import EmployeeInfo from "../offline-employee-detail/component/EmployeeInfo";
import {
  createEmployee,
  getMdr,
  getPreference,
} from "../offline-employee-detail/model";
import sha256 from "crypto-js/sha256";
import merchantManagement from "routes/merchantManagement";
import { PortalContext } from "App";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { stripLoginPrefix } from "../offline-employee-detail/utils/stripLoginPrefix";

const AddOfflineEmployee = ({ path, redirectFn }) => {
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [isLoading, setIsLoading] = useState(false);
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, trigger, reset, setValue } = formProps;
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const EMPLOYEE_LOCALE_KEY = "page.employees.";
  const [preferredLoginPrefix, setPreferredLoginPrefix] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  useLayoutEffect(() => {
    // handle merchant employee tab click "Add Offline Employee" and redirect to this page with merchant ID.
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]?.merchantId) {
      setValue("merchantId", detailPageState[path].merchantId);
    } else if (sessionState && sessionState[path]?.merchantId) {
      setValue("merchantId", sessionState[path].merchantId);
    }
  }, [detailPageState[path]]);

  useEffect(() => {
    const merchantPayfacPrefix = `${selectedMerchant?.payfacPrefix || ""}`.trim();
    setPreferredLoginPrefix(selectedMerchant ? merchantPayfacPrefix : "");
  }, [selectedMerchant]);

  const convertMdrListToTidmidList = (mdrList, defaultTid = "") => {
    console.log("mdrList", mdrList);
    const tidmidList = [];

    if (!Array.isArray(mdrList)) {
      return tidmidList;
    }

    mdrList.forEach((mdrObject) => {
      if (
        mdrObject.gatewaySwitchingInfoList &&
        Array.isArray(mdrObject.gatewaySwitchingInfoList)
      ) {
        mdrObject.gatewaySwitchingInfoList.forEach((gatewayInfo) => {
          // Only include entries that have a mid (merchant ID)
          if (gatewayInfo.mid && gatewayInfo.mid.trim() !== "") {
            tidmidList.push({
              cardType: gatewayInfo.cardType,
              paymentGateway: gatewayInfo.paymentGateway,
              tid: defaultTid, // Using default TID as it's not in the mdrList structure
              mid: gatewayInfo.mid.trim(),
            });
          }
        });
      }
    });

    return tidmidList;
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const onSubmit = async (formValue) => {
    const isValid = await trigger(undefined, { shouldFocus: true });
    if (!isValid) return;
    if (selectedMerchant && !selectedMerchant.payfacPrefix) return;
    try {
      setIsLoading(true);
      let employee;
      try {
        console.log("Create Employee formValue", formValue);
        const [mdrResp, preferenceResp] = await Promise.all([
          getMdr(formValue.merchantId),
          getPreference({ merchantId: formValue.merchantId }),
        ]);
        const mdrList = mdrResp?.data?.data?.mdrList;
        const preference = preferenceResp?.data?.data.preference;
        const tidmidList = convertMdrListToTidmidList(mdrList);
        employee = {
          tidmidList,
          preference,
        };
      } catch (error) {
        console.error("Error fetching mdr and preference:", error);
        snackbar(
          `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.fetchEmployeeFail")}: ${error.message}`,
          "error",
        );
        setIsLoading(false);
        return;
      }

      if (!employee?.tidmidList?.length) {
        snackbar(
            `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.fetchEmployeeFail")}: No MID found`,
            "error"
        );
        setIsLoading(false);
        return; 
      }

      const yedpayTid = "";
      const yedpayMid = employee.tidmidList[0].mid;
      const normalizedLoginName = stripLoginPrefix(
        formValue.email,
        preferredLoginPrefix,
      );
      const email = preferredLoginPrefix
        ? `${preferredLoginPrefix}-${normalizedLoginName}`
        : normalizedLoginName;

      const payload = {
        ...formValue,
        email,
        firstName: formValue.firstname,
        lastName: formValue.lastname,
        password: sha256(formValue.password).toString(),
        componentAllowed: formValue.permission,
        yedpayMid,
        yedpayTid,

        // hard-coded values for terminal app permission
        permission: ["APP_MPOS"],
        componentAllowed: ["APP_MPOS"],
      };
      // Remove uesless payload
      delete payload.confirmPassword;
      delete payload.permission;
      delete payload.firstname;
      delete payload.lastname;
      const createEmployeeResp = await createEmployee(payload);
      console.log("Create employee response", createEmployeeResp);
      if (createEmployeeResp?.data?.responseCode === "0000") {
        snackbar(
          t(
            EMPLOYEE_LOCALE_KEY +
            "employeeDetail.fetchMessage.createEmployeeSuccess",
          ),
          "success",
        );
        reset();
      } else {
        snackbar(
          t(
            EMPLOYEE_LOCALE_KEY +
            "employeeDetail.fetchMessage.createEmployeeFailed",
          ),
          "error",
        );
      }
    } catch (error) {
      console.error("Create Employee error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.createEmployeeFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setTimeout(() => {
        navigateToEmployeeListPage();
        setIsLoading(false);
        deleteCurrentTab();
      }, 1000);
    }
  };

  const navigateToEmployeeListPage = () => {
    const path = "/merchant-management/employees/offline-employees";
    const payload = {
      name: merchantManagement
        .find((tab) => tab.tabName === "Employees")
        .page.find((page) => page.path === path).name,
      path: path,
      refresh: "refresh",
    };
    redirectFn("redirectPage", payload);
  };

  const deleteCurrentTab = () => {
    const basePath = "/merchant-management/employees/add-offline-employee";
    const payload = {
      tabId: path, // exact current tab id (may include state suffix)
      name: merchantManagement
        .find((tab) => tab.tabName === "Employees")
        .page.find((page) => page.path === basePath).name,
      path: basePath,
    };
    console.log("deleteCurrentTab payload", payload);
    redirectFn("close", payload);
  };

  const submitButton = () => (
    <Stack
      direction="row"
      justifyContent="flex-end"
      gap={2}
      flexWrap="wrap"
      mt={3}
    >
      <ButtonMAS
        type="submit"
        isLoading={isLoading}
        disabled={isLoading}
      >
        {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.submit`)}
      </ButtonMAS>
    </Stack>
  );

  const defaultMerchant = React.useMemo(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    const pageState = detailPageState[path] || (sessionState && sessionState[path]);
    
    if (pageState?.merchantId) {
      return {
        key: pageState.merchantId,
        title: `${pageState.merchantId} - ${pageState.merchantName || ""}`,
        value: pageState.merchantId,
        payfacPrefix: pageState.payfacPrefix,
      };
    }
    return null;
  }, [detailPageState, path]);

  return (
    <Stack direction="column" sx={{ padding: "24px", background: "white" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormBuilder.Section
          title={t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.detail.subtitle.basicInformation`)}
          titleVariant="h6"
        >
          <EmployeeInfo
            formProps={formProps}
            isEdit={true}
            isLoading={isLoading}
            redirectFn={redirectFn}
            isCreate={true}
            defaultMerchant={defaultMerchant}
            preferredLoginPrefix={preferredLoginPrefix}
            onMerchantChange={setSelectedMerchant}
          />
        </FormBuilder.Section>
        <Divider style={{ margin: "24px 0" }} />
        {submitButton()}
      </form>
    </Stack>
  );
};

export default React.memo(AddOfflineEmployee);
